import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Clock, Circle, MapPin, Calendar, Image, Building2 } from '../icons/wp-icons';

// VITE_API_URL enthält bereits /api, daher nur die Base-URL extrahieren
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');

const STATUS_LABELS = {
  PLANUNG: 'Planung',
  IN_BEARBEITUNG: 'In Bearbeitung',
  ABGESCHLOSSEN: 'Abgeschlossen',
  ABGERECHNET: 'Abgerechnet',
};

const STATUS_COLORS = {
  PLANUNG: 'bg-blue-100 text-blue-700',
  IN_BEARBEITUNG: 'bg-yellow-100 text-yellow-700',
  ABGESCHLOSSEN: 'bg-green-100 text-green-700',
  ABGERECHNET: 'bg-gray-100 text-gray-700',
};

const MEILENSTEIN_ICONS = {
  OFFEN: <Circle className="w-4 h-4 text-gray-400" />,
  IN_ARBEIT: <Clock className="w-4 h-4 text-yellow-500" />,
  ERLEDIGT: <CheckCircle className="w-4 h-4 text-green-500" />,
};

export default function PublicProjekt() {
  const { token } = useParams();
  const [projekt, setProjekt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProjekt = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/projekt/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Projekt nicht gefunden');
        }
        setProjekt(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProjekt();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link ungültig</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!projekt) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Building2 className="w-4 h-4" />
            <span>{projekt.organisation}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{projekt.titel}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[projekt.status] || 'bg-gray-100 text-gray-700'}`}>
              {STATUS_LABELS[projekt.status] || projekt.status}
            </span>
            {projekt.ort && (
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <MapPin className="w-3.5 h-3.5" /> {projekt.ort}
              </span>
            )}
            {projekt.startDatum && (
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(projekt.startDatum).toLocaleDateString('de-DE')}
                {projekt.endDatum && ` – ${new Date(projekt.endDatum).toLocaleDateString('de-DE')}`}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 space-y-8">
        {/* Beschreibung */}
        {projekt.beschreibung && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Beschreibung</h2>
            <p className="text-gray-600 whitespace-pre-wrap">{projekt.beschreibung}</p>
          </div>
        )}

        {/* Fortschritt */}
        {projekt.meilensteine?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Fortschritt</h2>
              <span className="text-sm font-medium text-gray-500">{projekt.fortschrittProzent}%</span>
            </div>

            {/* Fortschrittsbalken */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
              <div
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${projekt.fortschrittProzent}%` }}
              />
            </div>

            {/* Meilensteine */}
            <div className="space-y-3">
              {projekt.meilensteine.map((m, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                  <div className="mt-0.5">{MEILENSTEIN_ICONS[m.status] || MEILENSTEIN_ICONS.OFFEN}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium ${m.status === 'ERLEDIGT' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {m.titel}
                      </span>
                      {m.faelligAm && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {new Date(m.faelligAm).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </div>
                    {m.beschreibung && (
                      <p className="text-xs text-gray-500 mt-1">{m.beschreibung}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fotos */}
        {projekt.fotos?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5" /> Fotos
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {projekt.fotos.map((foto) => (
                <div key={foto.id} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={`${API_BASE}${foto.url}`}
                    alt={foto.beschreibung || 'Projektfoto'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pt-4">
          Projektfortschritt bereitgestellt von {projekt.orgType === 'HEYKIKI' ? 'HeyKiki' : 'WerkPilot'}
        </div>
      </div>
    </div>
  );
}
